import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  HiX, 
  HiOutlineClock, 
  HiOutlineCalendar, 
  HiOutlineUser, 
  HiOutlineTag, 
  HiOutlinePaperClip, 
  HiOutlineChat,
  HiOutlineDocumentAdd,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineSave,
  HiExclamation,
  HiOutlinePlus,
  HiArchive,
  HiOutlineCheck,
  HiOutlineRefresh,
  HiOutlineStop
} from 'react-icons/hi';
import { db, storage } from '../../../../firebase/config';
import { doc, updateDoc, collection, addDoc, getDocs, query, orderBy, where, deleteDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useSelector } from 'react-redux';
import emailService from '../../../../emailjs/emailService';
import './TaskDetailModal.scss';

const TaskDetailModal = ({ task: initialTask, onClose, employees, onTaskUpdate, isUserView = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [localTask, setLocalTask] = useState({...initialTask});
  const [editedTask, setEditedTask] = useState({...initialTask});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  
  // Use refs to track mounted state
  const isMounted = useRef(true);

  const currentUser = useSelector(state => state.auth.user);

  // Fetch comments from the correct Firestore path
  const fetchComments = useCallback(async () => {
    if (!localTask?.id || !isMounted.current) return;
    
    try {
      setIsLoading(true);
      
      // Updated path: otherDBs (collection) --> taskComments (doc) --> taskComments (collection)
      const commentsRef = collection(db, 'otherDBs', 'taskComments', 'taskComments');
      const commentsQuery = query(
        commentsRef,
        where('taskID', '==', localTask.id),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(commentsQuery);
      
      if (isMounted.current) {
        const commentsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setComments(commentsData);
      }
    } catch (err) {
      console.error("Error fetching comments:", err);
      if (isMounted.current) {
        setError("Failed to load comments. Please try again.");
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, [localTask?.id]);

  const fetchAttachments = useCallback(async () => {
    if (!localTask?.id || !isMounted.current) return;
    
    try {
      setIsLoading(true);
      
      // Updated path: otherDBs (collection) --> taskAttachments (doc) --> taskAttachments (collection)
      const attachmentsRef = collection(db, 'otherDBs', 'taskAttachments', 'taskAttachments');
      const attachmentsQuery = query(
        attachmentsRef,
        where('taskID', '==', localTask.id),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(attachmentsQuery);
      
      if (isMounted.current) {
        const attachmentsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAttachments(attachmentsData);
      }
    } catch (err) {
      console.error("Error fetching attachments:", err);
      if (isMounted.current) {
        setError("Failed to load attachments. Please try again.");
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, [localTask?.id]);

  useEffect(() => {
    // Set isMounted to true when the component mounts
    isMounted.current = true;
    
    // Set isMounted to false when the component unmounts
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!localTask?.id) return;
    
    // Fetch initial data
    fetchComments();
    fetchAttachments();
    
    // Don't set up real-time listeners
  }, [localTask?.id, fetchComments, fetchAttachments]);

  useEffect(() => {
    if (initialTask) {
      setLocalTask(initialTask);
      setEditedTask({
        ...initialTask,
        roleBasedAssignment: initialTask.roleBasedAssignment || false,
        roleBasedAssignees: initialTask.roleBasedAssignees || []
      });
    }
  }, [initialTask]);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'No date set';
    
    let date;
    if (timestamp.seconds) {
      // Handle Firestore Timestamp
      date = new Date(timestamp.seconds * 1000);
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      date = new Date(timestamp);
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'Unknown';
    
    let date;
    if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      date = new Date(timestamp);
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAssigneeName = (assignee) => {
    // Check if assignee is an email
    if (typeof assignee === 'string' && assignee.includes('@')) {
      // Try to find the employee by email
      const employee = employees.find(emp => emp.email === assignee);
      return employee ? employee.name || assignee : assignee;
    }
    
    // Otherwise treat as ID
    const employee = employees.find(emp => emp.id === assignee);
    return employee ? employee.name || employee.email : 'Unknown User';
  };

  const getUserName = (userId) => {
    if (!userId) return 'Unknown';
    
    // If it's an email address
    if (typeof userId === 'string' && userId.includes('@')) {
      // Try to find the employee by email
      const employee = employees.find(emp => emp.email === userId);
      return employee && employee.name ? employee.name : userId;
    }
    
    // Otherwise try to find by ID
    const employee = employees.find(emp => emp.id === userId);
    
    // Return name, email, or the original userId as fallback
    return (employee && employee.name) ? employee.name : 
           (employee && employee.email) ? employee.email : userId;
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    
    if (!newComment.trim()) return;
    
    try {
      setIsSubmitting(true);
      
      // Comment object that matches the existing structure
      const commentData = {
        taskID: localTask.id,
        comment: newComment.trim(),
        commentedBy: currentUser.email,
        createdAt: new Date()
      };
      
      // Add to the proper collection path: otherDBs/taskComments/taskComments
      const commentCollectionRef = collection(db, 'otherDBs', 'taskComments', 'taskComments');
      const docRef = await addDoc(commentCollectionRef, commentData);
      
      console.log('Added comment with ID:', docRef.id);
      
      setNewComment('');
      
      // Send email notification about the comment
      await sendCommentNotification(localTask, newComment.trim());
      
      // Fetch updated comments
      fetchComments();
      
    } catch (error) {
      console.error("Error adding comment:", error);
      setError("Failed to add comment");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to send email notification when a comment is added
  const sendCommentNotification = async (task, commentText) => {
    console.log('Sending comment notification for task:', task);
    try {
      console.log('Sending comment notification for task:', task.id);
      
      // Get all assignees to notify
      let assigneeEmails = [];
      
      if (task.roleBasedAssignment && task.roleBasedAssignees) {
        assigneeEmails = task.roleBasedAssignees.map(a => a.email);
      } else if (task.assignees) {
        assigneeEmails = task.assignees;
      }
      console.log('Assignee emails:', assigneeEmails);
      // Include the task creator if they're not already in the list
      if (task.createdBy && task.createdBy.includes('@') && !assigneeEmails.includes(task.createdBy)) {
        assigneeEmails.push(task.createdBy);
      }
      
      // Remove the current user from the notification list (no need to notify themselves)
      // assigneeEmails = assigneeEmails.filter(email => email !== currentUser.email);
      
      console.log('Notification recipients:', assigneeEmails);
      
      if (assigneeEmails.length === 0) {
        console.log('No recipients to notify');
        return; // No one to notify
      }
      
      // Use the centralized email service to send notifications
      const result = await emailService.sendTaskCommentNotification(
        task,
        currentUser,
        commentText,
        assigneeEmails
      );
      
      console.log('Email notification result:', result);
      
    } catch (error) {
      console.error('Error sending comment notification:', error);
      // Don't throw the error - this is a secondary function and shouldn't block the main flow
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    
    try {
      // Delete from the proper collection path
      await deleteDoc(doc(db, 'otherDBs', 'taskComments', 'taskComments', commentId));
      
      // Fetch updated comments
      fetchComments();
    } catch (error) {
      console.error("Error deleting comment:", error);
      setError("Failed to delete comment");
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    
    if (!file) return;
    
    try {
      setIsUploading(true);
      setUploadProgress(0);
      
      // Upload file to Firebase Storage
      const storageRef = ref(storage, `task-attachments/${localTask.id}/${file.name}`);
      const uploadTask = uploadBytes(storageRef, file);
      
      // Get download URL after upload completes
      const snapshot = await uploadTask;
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      // Add attachment record to Firestore using the correct collection path
      const attachmentData = {
        taskID: localTask.id,
        fileName: file.name,
        title: file.name, // Add title field to match existing structure
        fileType: file.type,
        fileSize: file.size,
        file: downloadURL, // Use 'file' field for URL to match existing structure
        downloadURL: downloadURL, // Keep this for backward compatibility
        uploadedBy: currentUser.email,
        createdAt: new Date()
      };
      
      // Updated path: otherDBs (collection) --> taskAttachments (doc) --> taskAttachments (collection)
      const attachmentsCollectionRef = collection(db, 'otherDBs', 'taskAttachments', 'taskAttachments');
      await addDoc(attachmentsCollectionRef, attachmentData);
      
      // Reset file input
      setFile(null);
      setUploadProgress(100);
      
      // Fetch updated attachments
      fetchAttachments();
      
    } catch (error) {
      console.error("Error uploading file:", error);
      setError("Failed to upload file");
    } finally {
      setIsUploading(false);
      // Reset input by clearing the value
      const fileInput = document.getElementById('attachment-input');
      if (fileInput) fileInput.value = '';
    }
  };

  const handleDeleteAttachment = async (attachment) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) return;
    
    try {
      // Delete from Storage
      if (attachment.fileName) {
        const storageRef = ref(storage, `task-attachments/${attachment.taskID || localTask.id}/${attachment.fileName}`);
        await deleteObject(storageRef).catch(error => {
          console.error('Error deleting file from storage:', error);
        });
      }
      
      // Delete from Firestore using the correct collection path
      // Updated path: otherDBs (collection) --> taskAttachments (doc) --> taskAttachments (collection)
      await deleteDoc(doc(db, 'otherDBs', 'taskAttachments', 'taskAttachments', attachment.id));
      
      // Fetch updated attachments
      fetchAttachments();
    } catch (error) {
      console.error("Error deleting attachment:", error);
      setError("Failed to delete attachment");
    }
  };

  const handleSaveEdit = async () => {
    // User view may have limited edit capabilities
    if (isUserView && !canUserEdit()) {
      setError("You don't have permission to edit this task");
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');
      
      // Validate required fields
      if (!editedTask.title.trim()) {
        setError('Task title is required');
        setIsSubmitting(false);
        return;
      }
      
      // Format assignees from IDs to email addresses
      let assigneeEmails = [];
      if (editedTask.roleBasedAssignment) {
        assigneeEmails = editedTask.roleBasedAssignees?.map(item => item.email) || [];
      } else {
        assigneeEmails = editedTask.assignees?.map(id => {
          const employee = employees.find(emp => emp.id === id);
          return employee ? employee.email : null;
        }).filter(Boolean) || [];
      }
      
      // Process the date value correctly
      let endDate;
      if (editedTask.endDate && typeof editedTask.endDate === 'object') {
        if (editedTask.endDate.seconds) {
          // It's a Firestore timestamp
          endDate = new Date(editedTask.endDate.seconds * 1000);
        } else if (editedTask.endDate instanceof Date) {
          // It's already a Date object
          endDate = editedTask.endDate;
        }
      } else if (editedTask.dueDate) {
        // Use dueDate as fallback
        if (typeof editedTask.dueDate === 'object' && editedTask.dueDate.seconds) {
          endDate = new Date(editedTask.dueDate.seconds * 1000);
        } else if (editedTask.dueDate instanceof Date) {
          endDate = editedTask.dueDate;
        }
      }
      
      if (!endDate) {
        // Set a default if somehow both are missing
        endDate = new Date();
        endDate.setDate(endDate.getDate() + 1);
      }
      
      // Create update object
      const taskRef = doc(db, 'assignedTasks', localTask.id);
      const updateData = {
        title: editedTask.title,
        description: editedTask.description || '',
        details: editedTask.description || '',
        endDate: endDate,
        priority: editedTask.priority || 'Medium',
        assignees: assigneeEmails,
        lastUpdated: new Date(),
        lastUpdatedBy: currentUser.email || currentUser.uid,
        lastUpdatedIncident: 'Task details updated',
        roleBasedAssignment: editedTask.roleBasedAssignment || false,
        roleBasedAssignees: editedTask.roleBasedAssignment ? (editedTask.roleBasedAssignees || []) : []
      };
      
      await updateDoc(taskRef, updateData);
      
      setIsEditing(false);
      
      if (onTaskUpdate) {
        onTaskUpdate();
      }
      
    } catch (err) {
      console.error('Error updating task:', err);
      setError(`Failed to update task: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to determine if user can edit
  const canUserEdit = () => {
    // Logic to determine if user can edit
    // For example, users can only update status but not other fields
    return true; // Simplified for this example
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileType) => {
    if (fileType.includes('image')) return '🖼️';
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('document') || fileType.includes('word')) return '📝';
    if (fileType.includes('sheet') || fileType.includes('excel')) return '📊';
    if (fileType.includes('presentation') || fileType.includes('powerpoint')) return '📽️';
    return '📎';
  };

  const addRoleAssignment = () => {
    const newRoleAssignees = [...(editedTask.roleBasedAssignees || [])];
    newRoleAssignees.push({ email: '', role: 'Task Coordinator' });
    setEditedTask({
      ...editedTask,
      roleBasedAssignees: newRoleAssignees
    });
  };

  const updateRoleAssignment = (index, field, value) => {
    const newRoleAssignees = [...(editedTask.roleBasedAssignees || [])];
    newRoleAssignees[index] = {
      ...newRoleAssignees[index],
      [field]: value
    };
    setEditedTask({
      ...editedTask,
      roleBasedAssignees: newRoleAssignees
    });
  };

  const removeRoleAssignment = (index) => {
    if ((editedTask.roleBasedAssignees || []).length <= 1) return;
    const newRoleAssignees = editedTask.roleBasedAssignees.filter((_, i) => i !== index);
    setEditedTask({
      ...editedTask,
      roleBasedAssignees: newRoleAssignees
    });
  };

  // Function to get recipients for notifications
  const getNotificationRecipients = (taskData) => {
    let assigneeEmails = [];
    
    if (taskData.roleBasedAssignment && taskData.roleBasedAssignees) {
      assigneeEmails = taskData.roleBasedAssignees.map(a => a.email);
    } else if (taskData.assignees) {
      assigneeEmails = taskData.assignees;
    }
    
    // Include the task creator
    if (taskData.createdBy && taskData.createdBy.includes('@') && !assigneeEmails.includes(taskData.createdBy)) {
      assigneeEmails.push(taskData.createdBy);
    }
    
    // Remove duplicates
    assigneeEmails = [...new Set(assigneeEmails)];
    
    return assigneeEmails;
  };

  // Function to handle status change
  const handleStatusChange = async (newStatus) => {
    if (isChangingStatus) return;
    
    try {
      setIsChangingStatus(true);
      setError('');
      
      // Get the current status for the notification
      const currentStatus = localTask.status || 
        (localTask.statusNormalized === 'new' ? 'New task' : 
        localTask.statusNormalized === 'in-progress' ? 'In progress' : 
        'Completed');
      
      // Map the clicked status to the normalized status value
      let statusNormalized;
      let statusText;
      
      switch (newStatus) {
        case 'new':
          statusNormalized = 'new';
          statusText = 'New task';
          break;
        case 'in-progress':
          statusNormalized = 'in-progress';
          statusText = 'In progress';
          break;
        case 'completed':
          statusNormalized = 'completed';
          statusText = 'Completed';
          break;
        default:
          statusNormalized = 'new';
          statusText = 'New task';
      }
      
      // Create the update data
      const updateData = {
        statusNormalized,
        status: statusText,
        lastUpdated: new Date(),
        lastUpdatedBy: currentUser.email || currentUser.uid,
        lastUpdatedIncident: `Status changed to ${statusText}`
      };
      
      // Update the task in Firestore
      const taskRef = doc(db, 'assignedTasks', localTask.id);
      await updateDoc(taskRef, updateData);
      
      // Update local task state to reflect changes immediately
      setLocalTask(prevTask => ({
        ...prevTask,
        ...updateData
      }));
      
      // Also update the editedTask state to keep it in sync
      setEditedTask(prevEditedTask => ({
        ...prevEditedTask,
        ...updateData
      }));
      
      // Send notification
      const recipients = getNotificationRecipients(localTask);
      
      if (recipients.length > 0) {
        await emailService.sendTaskStatusUpdateNotification(
          localTask,
          currentUser,
          currentStatus,
          statusText,
          recipients
        );
      }
      
      // Update parent component
      if (onTaskUpdate) {
        onTaskUpdate();
      }
      
    } catch (error) {
      console.error('Error updating task status:', error);
      setError(`Failed to update task status: ${error.message}`);
    } finally {
      setIsChangingStatus(false);
    }
  };

  // Function to handle archiving
  const handleArchiveTask = async () => {
    if (isArchiving || !window.confirm('Are you sure you want to archive this task?')) return;
    
    try {
      setIsArchiving(true);
      setError('');
      
      // Update the task in Firestore to add liveStatus field
      const taskRef = doc(db, 'assignedTasks', localTask.id);
      await updateDoc(taskRef, {
        liveStatus: 'archived',
        lastUpdated: new Date(),
        lastUpdatedBy: currentUser.email || currentUser.uid,
        lastUpdatedIncident: 'Task archived'
      });
      
      // Send notification
      const recipients = getNotificationRecipients(localTask);
      
      if (recipients.length > 0) {
        await emailService.sendTaskArchiveNotification(
          localTask,
          currentUser,
          recipients
        );
      }
      
      // Close the modal and refresh the task list
      if (onTaskUpdate) {
        onTaskUpdate();
      }
      onClose();
      
    } catch (error) {
      console.error('Error archiving task:', error);
      setError(`Failed to archive task: ${error.message}`);
    } finally {
      setIsArchiving(false);
    }
  };

  // Function to handle deleting
  const handleDeleteTask = async () => {
    if (isDeleting || !window.confirm('Are you sure you want to delete this task? This action cannot be undone.')) return;
    
    try {
      setIsDeleting(true);
      setError('');
      
      // Get notification recipients before deleting the task
      const recipients = getNotificationRecipients(localTask);
      
      // Delete task comments
      const commentsRef = collection(db, 'otherDBs', 'taskComments', 'taskComments');
      const commentsQuery = query(commentsRef, where('taskID', '==', localTask.id));
      const commentsSnapshot = await getDocs(commentsQuery);
      
      const commentDeletePromises = commentsSnapshot.docs.map(commentDoc => 
        deleteDoc(doc(db, 'otherDBs', 'taskComments', 'taskComments', commentDoc.id))
      );
      
      // Delete task attachments
      const attachmentsRef = collection(db, 'taskAttachments');
      const attachmentsQuery = query(attachmentsRef, where('taskId', '==', localTask.id));
      const attachmentsSnapshot = await getDocs(attachmentsQuery);
      
      const attachmentDeletePromises = attachmentsSnapshot.docs.map(async attachmentDoc => {
        const attachmentData = attachmentDoc.data();
        
        // Delete file from storage
        if (attachmentData.fileName) {
          const storageRef = ref(storage, `taskAttachments/${localTask.id}/${attachmentData.fileName}`);
          await deleteObject(storageRef).catch(error => {
            console.error('Error deleting file from storage:', error);
          });
        }
        
        // Delete entry from Firestore
        return deleteDoc(doc(db, 'taskAttachments', attachmentDoc.id));
      });
      
      // Wait for all comment and attachment deletions to complete
      await Promise.all([...commentDeletePromises, ...attachmentDeletePromises]);
      
      // Delete the task document
      await deleteDoc(doc(db, 'assignedTasks', localTask.id));
      
      // Send notification
      if (recipients.length > 0) {
        await emailService.sendTaskDeleteNotification(
          localTask,
          currentUser,
          recipients
        );
      }
      
      // Close the modal and refresh the task list
      if (onTaskUpdate) {
        onTaskUpdate();
      }
      onClose();
      
    } catch (error) {
      console.error('Error deleting task:', error);
      setError(`Failed to delete task: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="task-detail-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditing ? 'Edit Task' : `Task Detail: ${localTask.title}`}</h2>
          <button className="close-button" onClick={onClose}>
            <HiX />
          </button>
          {/* <button onClick={()=> console.log(localTask)} className="btn btn-primary">Log</button> */}
        </div>
        
        {error && (
          <div className="error-message">
            <HiExclamation className="error-icon" />
            {error}
          </div>
        )}
        
        <div className="modal-body">
          {isEditing ? (
            <div className="edit-form">
              <div className="form-group">
                <label>Title</label>
                <input 
                  type="text" 
                  value={editedTask.title} 
                  onChange={e => setEditedTask({...editedTask, title: e.target.value})}
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="form-group">
                <label>Description</label>
                <textarea 
                  value={editedTask.description} 
                  onChange={e => setEditedTask({...editedTask, description: e.target.value})}
                  disabled={isSubmitting}
                  rows={6}
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Due Date</label>
                  <input 
                    type="date" 
                    value={editedTask.endDate && typeof editedTask.endDate === 'object' && editedTask.endDate.seconds 
                      ? new Date(editedTask.endDate.seconds * 1000).toISOString().split('T')[0]
                      : editedTask.dueDate && typeof editedTask.dueDate === 'object' && editedTask.dueDate.seconds
                        ? new Date(editedTask.dueDate.seconds * 1000).toISOString().split('T')[0]
                        : ''} 
                    onChange={e => setEditedTask({...editedTask, dueDate: new Date(e.target.value)})}
                    disabled={isSubmitting}
                  />
                </div>
                
                <div className="form-group">
                  <label>Priority</label>
                  <select 
                    value={editedTask.priority} 
                    onChange={e => setEditedTask({...editedTask, priority: e.target.value})}
                    disabled={isSubmitting}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
              
              <div className="form-group assignment-type">
                <label>Assignment Type</label>
                <div className="radio-group">
                  <div className="radio-option">
                    <input
                      type="radio"
                      id="individual-assignment"
                      checked={!editedTask.roleBasedAssignment}
                      onChange={() => setEditedTask({...editedTask, roleBasedAssignment: false})}
                      disabled={isSubmitting}
                    />
                    <label htmlFor="individual-assignment">Individual Assignment</label>
                  </div>
                  <div className="radio-option">
                    <input
                      type="radio"
                      id="role-based-assignment"
                      checked={editedTask.roleBasedAssignment}
                      onChange={() => setEditedTask({...editedTask, roleBasedAssignment: true})}
                      disabled={isSubmitting}
                    />
                    <label htmlFor="role-based-assignment">Role-Based Assignment</label>
                  </div>
                </div>
              </div>
              
              {!editedTask.roleBasedAssignment ? (
                <div className="form-group">
                  <label>Assignees</label>
                  <div className="assignees-list">
                    {employees.length > 0 ? employees.map(employee => (
                      <div className="assignee-item" key={employee.id}>
                        <input 
                          type="checkbox" 
                          id={`assignee-${employee.id}`} 
                          checked={editedTask.assignees?.includes(employee.id)}
                          onChange={() => {
                            const currentAssignees = editedTask.assignees || [];
                            const newAssignees = currentAssignees.includes(employee.id)
                              ? currentAssignees.filter(id => id !== employee.id)
                              : [...currentAssignees, employee.id];
                            setEditedTask({...editedTask, assignees: newAssignees});
                          }}
                          disabled={isSubmitting}
                        />
                        <label htmlFor={`assignee-${employee.id}`}>{employee.name || employee.email}</label>
                      </div>
                    )) : (
                      <div className="no-employees">No employees found</div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="form-group">
                  <label>Role-Based Assignments*</label>
                  <div className="role-assignments">
                    {(editedTask.roleBasedAssignees || []).map((assignment, index) => (
                      <div key={index} className="role-assignment-item">
                        <select
                          value={assignment.email}
                          onChange={(e) => updateRoleAssignment(index, 'email', e.target.value)}
                          disabled={isSubmitting}
                        >
                          <option value="">Select Employee</option>
                          {employees.map(employee => (
                            <option key={employee.id} value={employee.email}>
                              {employee.name || employee.email}
                            </option>
                          ))}
                        </select>
                        <select
                          value={assignment.role}
                          onChange={(e) => updateRoleAssignment(index, 'role', e.target.value)}
                          disabled={isSubmitting}
                        >
                          <option value="">Select Role</option>
                          <option value="Task Coordinator">Task Coordinator</option>
                          <option value="Reviewer">Reviewer</option>
                          <option value="Developer">Developer</option>
                          <option value="Designer">Designer</option>
                          <option value="Tester">Tester</option>
                          <option value="Project Manager">Project Manager</option>
                          <option value="Quality Assurance">Quality Assurance</option>
                        </select>
                        <button
                          type="button"
                          className="remove-role-button"
                          onClick={() => removeRoleAssignment(index)}
                          disabled={(editedTask.roleBasedAssignees || []).length <= 1 || isSubmitting}
                        >
                          <HiX />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="add-role-button"
                      onClick={addRoleAssignment}
                      disabled={isSubmitting}
                    >
                      <HiOutlinePlus />
                      Add Another Role
                    </button>
                  </div>
                </div>
              )}
              
              <div className="form-actions">
                <button 
                  className="cancel-button" 
                  onClick={() => {
                    setIsEditing(false);
                    setEditedTask({...localTask});
                    setError('');
                  }}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button 
                  className="submit-button" 
                  onClick={handleSaveEdit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="task-details-section">
                <div className="task-header">
                  <h3>{localTask.title}</h3>
                  <button className="edit-button" onClick={() => setIsEditing(true)}>
                    <HiOutlinePencil />
                    Edit
                  </button>
                </div>
                
                <div className="task-meta">
                  <div className="meta-item">
                    <HiOutlineCalendar className="meta-icon" />
                    <span className="meta-label">Due Date:</span> 
                    <span className="meta-value">{formatDate(localTask.dueDate)}</span>
                  </div>
                  
                  <div className="meta-item">
                    <HiOutlineClock className="meta-icon" />
                    <span className="meta-label">Status:</span>
                    <span className={`meta-value status-badge ${localTask.statusNormalized || localTask.status}`}>
                      {localTask.status || 
                       (localTask.statusNormalized === 'new' ? 'New task' : 
                        localTask.statusNormalized === 'in-progress' ? 'In progress' : 
                        'Completed')}
                    </span>
                  </div>
                  
                  <div className="meta-item">
                    <HiOutlineTag className="meta-icon" />
                    <span className="meta-label">Priority:</span>
                    <span className={`meta-value priority-badge ${localTask.priority ? localTask.priority.toLowerCase() : 'medium'}`}>
                      {localTask.priority || 'Medium'}
                    </span>
                  </div>
                </div>
                
                <div className="status-update-buttons">
                  <h4>Update Status</h4>
                  <div className="status-button-group">
                    <button 
                      className={`status-button new ${localTask.statusNormalized === 'new' ? 'active' : ''}`}
                      onClick={() => handleStatusChange('new')}
                      disabled={isChangingStatus || localTask.statusNormalized === 'new'}
                    >
                      <HiOutlineRefresh />
                      New
                    </button>
                    
                    <button 
                      className={`status-button in-progress ${localTask.statusNormalized === 'in-progress' ? 'active' : ''}`}
                      onClick={() => handleStatusChange('in-progress')}
                      disabled={isChangingStatus || localTask.statusNormalized === 'in-progress'}
                    >
                      <HiOutlineRefresh />
                      In Progress
                    </button>
                    
                    <button 
                      className={`status-button completed ${localTask.statusNormalized === 'completed' ? 'active' : ''}`}
                      onClick={() => handleStatusChange('completed')}
                      disabled={isChangingStatus || localTask.statusNormalized === 'completed'}
                    >
                      <HiOutlineCheck />
                      Completed
                    </button>
                  </div>
                </div>
                
                <div className="task-description">
                  <h4>Description</h4>
                  {localTask.details ? (
                    <div className="html-content" dangerouslySetInnerHTML={{ __html: localTask.details }}></div>
                  ) : (
                    <p>{localTask.description}</p>
                  )}
                </div>
                
                <div className="assignees-section">
                  <h4>Assignees</h4>
                  <div className="assignees-list">
                    {localTask.roleBasedAssignment && localTask.roleBasedAssignees && localTask.roleBasedAssignees.length > 0 ? (
                      localTask.roleBasedAssignees.map((roleAssignee, index) => (
                        <div className="assignee" key={index}>
                          <HiOutlineUser className="assignee-icon" />
                          <span className="assignee-name">{getAssigneeName(roleAssignee.email)}</span>
                          <span className="assignee-role">{roleAssignee.role}</span>
                        </div>
                      ))
                    ) : localTask.assignees && localTask.assignees.length > 0 ? (
                      localTask.assignees.map((assignee, index) => (
                        <div className="assignee" key={index}>
                          <HiOutlineUser className="assignee-icon" />
                          {getAssigneeName(assignee)}
                        </div>
                      ))
                    ) : (
                      <p className="no-assignees">No assignees yet</p>
                    )}
                  </div>
                </div>
                
                <div className="task-history">
                  <h4>Task History</h4>
                  <div className="history-item">
                    <span className="history-label">Created:</span>
                    <span className="history-value">
                      {formatDateTime(localTask.createdAt)} 
                      {localTask.createdBy && ` by ${getUserName(localTask.createdBy)}`}
                    </span>
                  </div>
                  {localTask.lastUpdatedAt && (
                    <div className="history-item">
                      <span className="history-label">Last Updated:</span>
                      <span className="history-value">
                        {formatDateTime(localTask.lastUpdatedAt)}
                        {localTask.lastUpdatedBy && ` by ${getUserName(localTask.lastUpdatedBy)}`}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="attachments-section">
                <h4>
                  <HiOutlinePaperClip className="section-icon" />
                  Attachments ({attachments.length})
                </h4>
                
                <div className="attachment-upload">
                  <input 
                    type="file" 
                    id="attachment-input"
                    onChange={handleFileChange}
                    disabled={isUploading}
                  />
                  {file && (
                    <button 
                      className="upload-button"
                      onClick={handleFileUpload}
                      disabled={isUploading}
                    >
                      {isUploading ? (
                        <>
                          <div className="loading-spinner-small"></div>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <HiOutlineDocumentAdd />
                          Upload
                        </>
                      )}
                    </button>
                  )}
                </div>
                
                {isUploading && (
                  <div className="upload-progress">
                    <div 
                      className="progress-bar" 
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                    <span>{uploadProgress}%</span>
                  </div>
                )}
                
                <div className="attachments-list">
                  {attachments.length > 0 ? (
                    attachments.map(attachment => (
                      <div className="attachment-item" key={attachment.id}>
                        <div className="attachment-info">
                          <HiOutlinePaperClip className="attachment-icon" />
                          <div className="attachment-details">
                            <a 
                              href={attachment.file || attachment.downloadURL} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="attachment-name"
                            >
                              {attachment.fileName || attachment.title || 'Download Attachment'}
                            </a>
                            <span className="attachment-meta">
                              {attachment.fileSize ? formatFileSize(attachment.fileSize) + ' • ' : ''}
                              {attachment.uploadedBy ? 'Uploaded by ' + getUserName(attachment.uploadedBy) + ' • ' : ''}
                              {formatDateTime(attachment.createdAt)}
                            </span>
                          </div>
                        </div>
                        <button 
                          className="delete-button"
                          onClick={() => handleDeleteAttachment(attachment)}
                        >
                          <HiOutlineTrash />
                        </button>
                      </div>
                    ))
                  ) : (
                    <p className="no-attachments">No attachments yet</p>
                  )}
                </div>
              </div>
              
              <div className="comments-section">
                <h4>
                  <HiOutlineChat className="section-icon" />
                  Comments ({comments.length})
                </h4>
                
                <form className="comment-form" onSubmit={handleAddComment}>
                  <textarea 
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={e => setNewComment(e.target.value)}
                    disabled={isSubmitting}
                  ></textarea>
                  <button 
                    type="submit" 
                    className="comment-submit"
                    disabled={!newComment.trim() || isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="loading-spinner-small"></div>
                        Posting...
                      </>
                    ) : (
                      <>
                        <HiOutlineSave />
                        Post
                      </>
                    )}
                  </button>
                </form>
                
                <div className="comments-list">
                  {comments.length > 0 ? (
                    comments.map(comment => (
                      <div className="comment-item" key={comment.id}>
                        <div className="comment-header">
                          <div className="commenter-info">
                            <span className="commenter-name">
                              {getUserName(comment.commentedBy)}
                            </span>
                            <span className="comment-date">
                              {formatDateTime(comment.createdAt)}
                            </span>
                          </div>
                          {(currentUser.email === comment.commentedBy || currentUser.isAdmin) && (
                            <button 
                              className="delete-comment"
                              onClick={() => handleDeleteComment(comment.id)}
                            >
                              <HiOutlineTrash />
                            </button>
                          )}
                        </div>
                        <div className="comment-content">
                          {comment.comment}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="no-comments">No comments yet</p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        
        <div className="task-actions">
          <button 
            className="archive-button" 
            onClick={handleArchiveTask}
            disabled={isArchiving}
          >
            {isArchiving ? (
              <>
                <div className="loading-spinner-small"></div>
                Archiving...
              </>
            ) : (
              <>
                <HiArchive />
                Archive Task
              </>
            )}
          </button>
          
          <button 
            className="delete-button" 
            onClick={handleDeleteTask}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <div className="loading-spinner-small"></div>
                Deleting...
              </>
            ) : (
              <>
                <HiOutlineTrash />
                Delete Task
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TaskDetailModal; 